"use client"
import {
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    CircularProgress,
} from "@mui/material"
import { ChevronUpIcon, ChevronDownIcon } from "lucide-react"
import type { Column, TableProps } from "./types"

export function Table<T>({
    columns,
    data,
    keyExtractor,
    onRowClick,
    isLoading = false,
    emptyMessage = "No data available",
    sortColumn,
    sortDirection = "asc",
    onSort,
    rowActions,
}: TableProps<T>) {
    // Función para ordenar los datos localmente si no se proporciona onSort
    const getSortedData = () => {
        if (!sortColumn || !onSort) {
            return data
        }

        // Si onSort está definido, asumimos que el ordenamiento se maneja externamente
        return data
    }

    const handleSort = (columnId: string) => {
        if (onSort) {
            onSort(columnId)
        }
    }

    // Modificar la función renderSortIcon para mostrar solo flechas hacia arriba o abajo
    const renderSortIcon = (column: Column<T>) => {
        if (!column.sortable) return null

        if (sortColumn === column.id) {
            return sortDirection === "asc" ? (
                <ChevronUpIcon size={16} className="ml-1 text-primary" />
            ) : (
                <ChevronDownIcon size={16} className="ml-1 text-primary" />
            )
        }

        // No mostrar ningún icono para columnas que no están ordenadas actualmente
        return null
    }

    const sortedData = getSortedData()

    return (
        <TableContainer>
            <MuiTable sx={{ minWidth: 650 }} aria-label="data table">
                <TableHead>
                    <TableRow
                        sx={{
                            "& th": {
                                fontWeight: "bold",
                                bgcolor: "#f9f9f9",
                                borderBottom: "1px solid #e0e0e0",
                            },
                        }}
                    >
                        {columns.map((column) => (
                            <TableCell
                                key={column.id}
                                align={column.align || "left"}
                                sx={{
                                    width: column.width,
                                    minWidth: column.width, // Añadir minWidth para mantener el ancho
                                    maxWidth: column.width, // Añadir maxWidth para mantener el ancho
                                    cursor: column.sortable ? "pointer" : "default",
                                    "&:hover": column.sortable
                                        ? {
                                            backgroundColor: "#f0f0f0",
                                        }
                                        : {},
                                    transition: "background-color 0.2s",
                                }}
                                onClick={() => column.sortable && handleSort(column.id)}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: column.align === "right" ? "flex-end" : "flex-start",
                                        // Añadir un ancho fijo para el contenido
                                        width: "100%",
                                    }}
                                >
                                    <Typography
                                        noWrap
                                        component="span"
                                        sx={{
                                            flexGrow: 1,
                                            fontWeight: "bold", // Añadir esta línea para restaurar la negrita
                                        }}
                                    >
                                        {column.label}
                                    </Typography>
                                    {renderSortIcon(column)}
                                </Box>
                            </TableCell>
                        ))}
                        {rowActions && <TableCell></TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading ? (
                        <TableRow>
                            <TableCell colSpan={columns.length + (rowActions ? 1 : 0)} align="center" sx={{ py: 4 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Loading...
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : sortedData.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length + (rowActions ? 1 : 0)} align="center" sx={{ py: 4 }}>
                                <Typography variant="body2">{emptyMessage}</Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        sortedData.map((item) => (
                            <TableRow
                                key={keyExtractor(item)}
                                hover
                                onClick={() => onRowClick && onRowClick(item)}
                                sx={{
                                    cursor: onRowClick ? "pointer" : "default",
                                    "&:hover": {
                                        backgroundColor: onRowClick ? "rgba(0, 0, 0, 0.04)" : undefined,
                                    },
                                }}
                            >
                                {columns.map((column) => (
                                    <TableCell key={`${keyExtractor(item)}-${column.id}`} align={column.align || "left"}>
                                        {column.accessor(item)}
                                    </TableCell>
                                ))}
                                {rowActions && <TableCell align="right">{rowActions(item)}</TableCell>}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </MuiTable>
        </TableContainer>
    )
}

